import { useState } from "react";
import { TESTIMONIALS, BUSINESS } from "../../../mocks/businessData";
import { useScrollReveal } from "../../../hooks/useScrollReveal";

export default function TestimonialsSection() {
  const [featured, setFeatured] = useState(0);
  const ref = useScrollReveal();

  return (
    <section className="bg-stone-950 overflow-hidden" ref={ref}>
      {/* ── Header strip ── */}
      <div className="border-b border-stone-800 py-16 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div className="reveal">
            <p className="text-amber-500 text-xs tracking-[0.40em] uppercase font-semibold mb-4">
              Client Voices
            </p>
            <h2 className="font-serif font-light text-4xl md:text-6xl text-white leading-tight">
              What Our Clients
              <span className="block italic text-amber-300">Are Saying</span>
            </h2>
          </div>
          <div className="reveal reveal-delay-2 flex flex-col items-start md:items-end gap-3">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-500 text-base" />
                </span>
              ))}
            </div>
            <p className="text-stone-400 text-sm">
              <strong className="text-white text-2xl font-serif">{BUSINESS.reviewCount}</strong>
              <span className="ml-2">Five-Star Reviews on Google</span>
            </p>
            <a
              href={BUSINESS.google}
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-600 hover:text-amber-400 text-xs tracking-widest uppercase font-semibold cursor-pointer transition-colors flex items-center gap-1.5"
            >
              <i className="ri-google-fill" />
              Read All Reviews
            </a>
          </div>
        </div>
      </div>

      {/* ── Featured Testimonial ── */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="reveal relative">
          {/* Quote mark */}
          <div className="absolute -top-4 left-0 quote-mark select-none pointer-events-none">&ldquo;</div>
          <div className="relative z-10 pl-0 md:pl-8 pt-8">
            <p className="font-serif font-light text-2xl md:text-4xl text-white/90 leading-relaxed italic mb-8 max-w-4xl">
              {TESTIMONIALS[featured].text}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-amber-500 text-xs" />
                  </span>
                ))}
              </div>
              <div className="w-12 h-px bg-amber-700/50" />
              <p className="text-amber-400 font-semibold text-sm tracking-widest uppercase">
                {TESTIMONIALS[featured].name}
              </p>
              <span className="text-stone-600 text-xs">· Verified Google Review</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3-Card Selector ── */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-stone-800">
          {TESTIMONIALS.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setFeatured(i)}
              className={`reveal reveal-delay-${i + 1} text-left p-8 transition-all duration-300 cursor-pointer group ${
                featured === i
                  ? "bg-amber-900/30 border-b-2 border-amber-500"
                  : "bg-stone-900/60 hover:bg-stone-900"
              }`}
            >
              {/* Stars */}
              <div className="flex items-center gap-0.5 mb-4">
                {[...Array(5)].map((_, j) => (
                  <span key={j} className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-star-fill text-amber-500 text-xs" />
                  </span>
                ))}
              </div>
              <p className={`text-sm leading-relaxed mb-4 line-clamp-3 transition-colors duration-200 ${
                featured === i ? "text-white/90" : "text-stone-400 group-hover:text-stone-300"
              }`}>
                &ldquo;{t.text}&rdquo;
              </p>
              <div className="flex items-center gap-2">
                <div className={`w-1.5 h-1.5 rounded-full transition-colors ${featured === i ? "bg-amber-500" : "bg-stone-600"}`} />
                <p className={`text-xs font-semibold tracking-wide uppercase transition-colors ${featured === i ? "text-amber-400" : "text-stone-500"}`}>
                  {t.name}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="reveal mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={BUSINESS.google}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-luxury inline-flex items-center gap-2 border border-stone-700 hover:border-amber-600 text-stone-300 hover:text-amber-300 text-xs tracking-[0.22em] uppercase font-semibold px-8 py-4 rounded-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-google-fill text-lg" />
            Read All {BUSINESS.reviewCount} Google Reviews
          </a>
          <a
            href="#quote-form"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-luxury inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-xs tracking-[0.22em] uppercase font-semibold px-8 py-4 rounded-sm cursor-pointer whitespace-nowrap"
          >
            Schedule Your Pickup
          </a>
        </div>
      </div>
    </section>
  );
}
